import { planApp, type PlanResult } from '../api/build';
import { useDraft } from './draft';
import type { AppPlan } from '@/webapp/plan/planTypes';

const FAILURE_REMEMBERED_MS = 2 * 60 * 1000;
let lastFailure: { idea: string; error: unknown; at: number } | null = null;

/**
 * The server's tailoring of this idea — asked for at most once per idea, whatever the screen
 * does meanwhile.
 *
 * `/plan` charges before it answers and takes 10–40s, and the screen can be left and reopened
 * in that time (browser back or forward). Tied to the mounted screen, the answer was thrown
 * away when it landed on an unmounted one, and the remount paid for it again — or, reopened
 * before it landed, asked a second time alongside it. So the call belongs to the draft: one in
 * flight per idea, shared by every mount, and its answer kept there even if no screen is left
 * to show it.
 */
export function tailorPlan(idea: string): Promise<PlanResult> {
  /*
   * A request that just FAILED for this idea is not asked again by the screen that opens next: the
   * idea screen waits on the plan now (2026-10-04) and moves on when it fails, and a second request
   * from the plan screen would be a second capped (or paid) /plan for the same answer. It gets the
   * same error instead, and shows its usual line about it.
   */
  if (lastFailure && lastFailure.idea === idea && Date.now() - lastFailure.at < FAILURE_REMEMBERED_MS) {
    return Promise.reject(lastFailure.error);
  }
  const draft = useDraft.getState();
  if (draft.tailoring?.idea === idea) return draft.tailoring.result;
  // Already tailored for this idea: the same plan, not a second (capped or paid) request for it.
  if (draft.tailored?.idea === idea) return Promise.resolve({ plan: draft.tailored.plan, refined: true } as PlanResult);
  /*
   * The photos already attached, by name (contract C2), so the plan can offer a photo hero and a
   * gallery. Rarely any yet: this runs as the features step opens and photos are added on the
   * content step after it. Deliberately NOT part of the cache key (review of lane/build-publish,
   * 2026-09-30): /plan charges before it answers, so a key that counted the photos bought a second
   * tailoring for every photo added after the first — and the /plan handler live today ignores
   * `media`, so it would sell the same plan again.
   */
  const result = Promise.resolve(planApp(idea, { media: draft.attachedMedia.map((m) => m.name) }));
  draft.setTailoring({ idea, result });
  const settle = (plan: AppPlan | null | undefined): void => {
    const now = useDraft.getState();
    if (now.tailoring?.result === result) now.setTailoring(null);
    // Kept while the draft is still about this idea — the only screen that could use it.
    if (plan && now.description === idea) now.setTailored({ idea, plan });
  };
  result.then((r) => settle(r?.plan), (error: unknown) => {
    lastFailure = { idea, error, at: Date.now() };
    settle(null);
  });
  return result;
}

/** For tests: forget a remembered failure. */
export function _forgetTailorFailure(): void {
  lastFailure = null;
}
